import { IUser } from '@common/interfaces/user'
import { openTimelineFreshAt, withOwnRowsOnly } from '../redux/channels/offline'
import type { IChannelsState } from '../redux/channels/reducer'

/**
 * The on-device copy of AIM that lets the app open with content instead of a spinner — and
 * open at all with no network. IndexedDB rather than localStorage: a few timelines of rich
 * messages is more than localStorage should hold, and writing it must not block the main thread.
 *
 * One record per signed-in identity, deleted on sign-out: it holds message bodies.
 */

const DB_NAME = 'atlas-offline'
const STORE = 'snapshots'
/** 2: drops timelines saved with another conversation's deep-link window merged in. */
export const SNAPSHOT_VERSION = 2
/** Per cached timeline; the rest reloads when the conversation is opened. */
export const SNAPSHOT_MESSAGES_PER_TIMELINE = 50

export type IChannelsSnapshot = Pick<
  IChannelsState,
  | 'channels'
  | 'recentConversations'
  | 'conversationGroups'
  | 'messageCache'
  | 'messageCacheOrder'
  | 'channelDetailCache'
>

export interface IOfflineSnapshot {
  version: number
  savedAt: number
  user: IUser
  channels: IChannelsSnapshot
}

const openDatabase = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is unavailable'))
      return
    }
    const request = indexedDB.open(DB_NAME, 1)
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE)
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'))
  })

const withStore = async <T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  const db = await openDatabase()
  try {
    return await new Promise<T>((resolve, reject) => {
      const request = run(db.transaction(STORE, mode).objectStore(STORE))
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
    })
  } finally {
    db.close()
  }
}

/** The key for one identity: a portfolio user working as a client is a different view. */
export const snapshotKey = (userId: string, clientId?: string | null): string =>
  clientId ? `${userId}:${clientId}` : userId

export const readSnapshot = async (key: string): Promise<IOfflineSnapshot | null> => {
  try {
    const snapshot = await withStore<IOfflineSnapshot | undefined>(
      'readonly',
      (store) => store.get(key) as IDBRequest<IOfflineSnapshot | undefined>,
    )
    return snapshot?.version === SNAPSHOT_VERSION ? snapshot : null
  } catch {
    return null
  }
}

export const writeSnapshot = async (key: string, snapshot: IOfflineSnapshot): Promise<void> => {
  try {
    await withStore('readwrite', (store) => store.put(snapshot, key))
  } catch {
    // Private mode or storage pressure: the app simply opens from the network next time.
  }
}

/** Everything, for every identity: sign-out on a shared device must leave nothing behind. */
export const clearSnapshots = async (): Promise<void> => {
  try {
    await withStore('readwrite', (store) => store.clear())
  } catch {
    // Nothing stored, or storage unavailable.
  }
}

/** The open conversation lives in `messages`, not the cache, until it is left. */
const withOpenTimeline = (channels: IChannelsState) => {
  const open = channels.messages
  if (!open.channelId || open.hasNewer || !open.items.length) {
    return { cache: channels.messageCache, order: channels.messageCacheOrder }
  }
  return {
    cache: {
      ...channels.messageCache,
      [open.channelId]: {
        items: open.items.filter((item) => !item.isPending && !item.isFailed),
        hasMore: open.hasMore,
        nextCursor: open.nextCursor,
        nextCursorId: open.nextCursorId,
        cachedAt: openTimelineFreshAt(channels),
      },
    },
    order: [open.channelId, ...channels.messageCacheOrder.filter((id) => id !== open.channelId)],
  }
}

/** The part of the channels slice worth keeping, trimmed so the record stays small. */
export const pickChannelsSnapshot = (channels: IChannelsState): IChannelsSnapshot => {
  const { cache, order } = withOpenTimeline(channels)
  const detailCache =
    channels.activeChannel && !channels.activeChannel.isCached
      ? { ...channels.channelDetailCache, [channels.activeChannel.id]: channels.activeChannel }
      : channels.channelDetailCache
  return {
    channels: channels.channels,
    recentConversations: channels.recentConversations,
    conversationGroups: channels.conversationGroups,
    messageCacheOrder: order,
    channelDetailCache: detailCache,
    messageCache: Object.fromEntries(
      Object.entries(withOwnRowsOnly(cache)).map(([channelId, timeline]) => {
        const kept = timeline.items.slice(-SNAPSHOT_MESSAGES_PER_TIMELINE)
        if (kept.length === timeline.items.length) return [channelId, timeline]
        // Trimmed from the top, so paging back resumes from the oldest message kept — the
        // cursor ListMessages would have returned for this window.
        const oldest = kept[0]
        return [
          channelId,
          {
            ...timeline,
            items: kept,
            hasMore: true,
            nextCursor: oldest?.createdAt ?? null,
            nextCursorId: oldest?.id ?? null,
          },
        ]
      }),
    ),
  }
}
